import { useTheme, type ThemePreference } from "../hooks/useTheme";

function ThemeToggleButton() {
  const { theme, cycleTheme } = useTheme();

  const label: Record<ThemePreference, string> = {
    system: "System",
    light: "Light",
    dark: "Dark",
  };

  return (
    <button
      onClick={cycleTheme}
      title={`Theme: ${label[theme]} — click to cycle`}
      className="absolute top-4 right-4 p-2 rounded-lg text-theme-muted hover:text-theme-primary hover:bg-theme-overlay transition-colors"
      aria-label="Toggle theme"
    >
      {theme === "dark" ? (
        /* Moon */
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      ) : theme === "light" ? (
        /* Sun */
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        /* Monitor / System */
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      )}
    </button>
  );
}

/** Microsoft "four squares" logo mark */
function MicrosoftLogo() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 21 21"
      fill="none"
      className="shrink-0"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export default function Login() {
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center p-6 relative overflow-hidden bg-black">
      <ThemeToggleButton />

      {/* Background image — DCVFD login hero at 50% opacity on black */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "url(https://assets.dcvfd.org/images/dcvfd-data-login.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.5,
        }}
      />
      {/* Dark gradient overlay for legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.5) 100%)",
        }}
      />

      <div className="w-full max-w-sm flex flex-col items-center relative z-10">
        {/* Glass card */}
        <div className="w-full glass rounded-2xl p-6 sm:p-8 flex flex-col items-center shadow-2xl shadow-black/10 dark:shadow-black/40">
          {/* Ember mark on the login surface — DCVFD patch retained on the
              collapsed sidebar. */}
          <div className="mb-5 drop-shadow-lg">
            <img
              src="https://assets.dcvfd.org/ember/EmberAI.png"
              alt="Ember"
              width={120}
              height={120}
              className="block object-contain"
            />
          </div>

          <p className="text-zinc-600 dark:text-zinc-300 text-xs mb-8 text-center leading-relaxed font-medium">
            DCVFD Personnel Only
          </p>

          {/* Sign in button */}
          <div className="w-full">
            <a
              href="/api/auth/entra/login"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "/api/auth/entra/login";
              }}
              className="group flex w-full items-center justify-center gap-3 rounded-xl bg-dcvfd px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-dcvfd/20 hover:bg-dcvfd-light hover:shadow-dcvfd/30 active:bg-dcvfd-dark active:scale-[0.98] min-h-[52px]"
            >
              <MicrosoftLogo />
              <span>Sign in with Microsoft</span>
            </a>
          </div>
        </div>

        <p className="mt-6 text-xs text-zinc-100 dark:text-zinc-300 text-center drop-shadow">
          Access restricted to DCVFD personnel.
        </p>
      </div>

      <p className="absolute bottom-6 text-xs text-zinc-100 dark:text-zinc-300 text-center z-10 drop-shadow">
        &copy; {new Date().getFullYear()} Dale City Volunteer Fire Department,
        Inc.
      </p>
    </div>
  );
}
